import { Component, Inject, OnDestroy, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormArray, FormBuilder, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { MatDialog, MatDialogRef, MAT_DIALOG_DATA, MatDialogModule } from '@angular/material/dialog';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatDatepickerModule } from '@angular/material/datepicker';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { Editor, NgxEditorModule, Toolbar, toHTML } from 'ngx-editor';
import { Subscription } from 'rxjs';

import { ContasReceberGridItemStruct } from '../../../../../shared/services/structs/financas/contas-receber-grid-item.struct';
import { SelectPesquisavelComponent } from '../../../../../shared/components/select-pesquisavel/select-pesquisavel.component';
import { AbntPipe } from '../../../../../shared/pipes/abnt.pipe';
import { RecebimentoContasReceberParcelaService } from '../../../../../shared/services/apis/financas/recebimento-contas-receber-parcela.service';
import { RecebimentoContasReceberRequest, RecebimentoParcelaItemRequest } from '../../../../../shared/services/requests/financas/recebimento-contas-receber.request';
import { AlertService, AlertType } from '../../../../../shared/services/alert.service';
import { MaskService } from '../../../../../shared/services/mask.service';
import { NgxCurrencyDirective } from 'ngx-currency';
import { PrepararRecebimentoParcelaItemStruct } from '../../../../../shared/services/structs/financas/preparar-recebimento-parcela-item.struct';
import { UtilService } from '../../../../../shared/services/util.service';
import { ContaFinanceiraEncargosConfig, EncargosUtil } from '../../../../utils/encargos.util';
import { ConfirmModalComponent } from '../../../../../shared/components/confirm-modal/confirm-modal.component';
import { StatusIntegracaoBoletoEnum } from '../../../../../shared/enums/integracao-financas-banco/status-integracao-boleto.enum';
import { AlertCardComponent } from '../../../../../shared/components/alert-card/alert-card.component';

export interface ReceberContasDialogData {
    parcelas: ContasReceberGridItemStruct[];
    listaCategorias: any[];
    listaContasFinanceiras: ContaFinanceiraEncargosConfig[];
}

@Component({
    selector: 'app-receber-contas-dialog',
    standalone: true,
    imports: [
        CommonModule,
        ReactiveFormsModule,
        MatDialogModule,
        MatButtonModule,
        MatIconModule,
        MatFormFieldModule,
        MatInputModule,
        MatDatepickerModule,
        MatProgressSpinnerModule,
        NgxEditorModule,
        SelectPesquisavelComponent,
        AbntPipe,
        NgxCurrencyDirective,
        AlertCardComponent
    ],
    templateUrl: './receber-contas-dialog.component.html',
    styleUrls: ['./receber-contas-dialog.component.css']
})
export class ReceberContasDialogComponent implements OnInit, OnDestroy {
    form: FormGroup;
    editor: Editor;
    toolbar: Toolbar = [
        ['bold', 'italic'],
        ['underline', 'strike'],
        ['ordered_list', 'bullet_list'],
        [{ heading: ['h1', 'h2', 'h3'] }],
        ['text_color', 'background_color'],
        ['align_left', 'align_center', 'align_right', 'align_justify'],
    ];

    currencyMask: any;
    isSaving = false;
    isLoading = true;
    totalRecebido = 0;

    /** Dados originais retornados pelo backend (guardados para recalcular ao trocar conta/data) */
    private parcelasPreparadas: PrepararRecebimentoParcelaItemStruct[] = [];
    private subscriptions = new Subscription();

    constructor(
        public dialogRef: MatDialogRef<ReceberContasDialogComponent>,
        @Inject(MAT_DIALOG_DATA) public data: ReceberContasDialogData,
        private fb: FormBuilder,
        private recebimentoService: RecebimentoContasReceberParcelaService,
        private alertService: AlertService,
        private maskService: MaskService,
        private dialog: MatDialog
    ) {
        this.editor = new Editor();
        this.currencyMask = this.maskService.getCurrencyMask();

        this.form = this.fb.group({
            idContaFinanceira: [null, Validators.required],
            dataRecebimento: [new Date(), Validators.required],
            observacoesHtml: [null],
            parcelas: this.fb.array([])
        });
    }

    ngOnInit(): void {
        this.prepararRecebimento();

        // Recalcula encargos quando conta de recebimento ou data mudar
        this.subscriptions.add(
            this.form.get('idContaFinanceira')!.valueChanges.subscribe(() => {
                this.recalcularEncargosParaTodasParcelas();
            })
        );
        this.subscriptions.add(
            this.form.get('dataRecebimento')!.valueChanges.subscribe(() => {
                this.recalcularEncargosParaTodasParcelas();
            })
        );

        this.subscriptions.add(
            this.parcelasFormArray.valueChanges.subscribe(() => {
                this.calcularTotal();
            })
        );
    }

    ngOnDestroy(): void {
        this.editor.destroy();
        this.subscriptions.unsubscribe();
    }

    get parcelasFormArray(): FormArray {
        return this.form.get('parcelas') as FormArray;
    }

    // ─── Carregamento Inicial ─────────────────────────────────────────────────

    private prepararRecebimento(): void {
        const req = {
            idsParcelas: this.data.parcelas.map(p => p.idContasReceberParcela)
        };

        this.recebimentoService.preparar(req).subscribe({
            next: (res) => {
                this.isLoading = false;
                if (res.isError) {
                    this.alertService.show('Erro', res.errorDescription, AlertType.error);
                    this.fechar();
                    return;
                }
                this.parcelasPreparadas = res.parcelas;
                this.popularParcelasPreparadas(res.parcelas);
                this.calcularTotal();
            },
            error: (err) => {
                this.isLoading = false;
                this.alertService.show('Erro', err.message || 'Erro ao carregar dados.', AlertType.error);
                this.fechar();
            }
        });
    }

    private popularParcelasPreparadas(parcelas: PrepararRecebimentoParcelaItemStruct[]): void {
        parcelas.forEach(item => {
            const jurosPrevisto = UtilService.arredondarAbnt(item.valorJurosPrevisto || 0, this.currencyMask.precision);
            const multaPrevista = UtilService.arredondarAbnt(item.valorMultaPrevisto || 0, this.currencyMask.precision);
            const acrescimoPrevisto = item.valorAcrescimoPrevisto || 0;
            const descontoPrevisto = item.valorDescontoPrevisto || 0;
            const total = UtilService.arredondarAbnt(
                item.valorPrincipalEmAberto + jurosPrevisto + multaPrevista + acrescimoPrevisto - descontoPrevisto,
                this.currencyMask.precision
            );
            let encargosFixos = UtilService.arredondarAbnt(
                jurosPrevisto + multaPrevista + acrescimoPrevisto - descontoPrevisto,
                this.currencyMask.precision
            );
            if (encargosFixos < 0) encargosFixos = 0;

            const formObj = this.fb.group({
                idContasReceberParcela: [item.idContasReceberParcela],
                idCategoriaFinanceira: [item.idCategoriaFinanceira, Validators.required],
                numeroDocumento: [item.numeroDocumento],
                cliente: [item.cliente],
                vencimento: [item.vencimento],
                valorOriginal: [item.valorOriginal],
                valorAReceber: [item.valorPrincipalEmAberto],
                valorMulta: [multaPrevista],
                valorJuros: [jurosPrevisto],
                valorDesconto: [item.valorDescontoPrevisto || 0],
                valorAcrescimo: [item.valorAcrescimoPrevisto || 0],
                valorRecebido: [total > 0 ? total : 0, [Validators.required, Validators.min(encargosFixos)]],
                isValido: [true]
            });

            formObj.get('valorRecebido')?.valueChanges.subscribe(() => {
                this.calcularTotal();
            });

            this.parcelasFormArray.push(formObj);
        });
    }

    // ─── Cálculo de Encargos ──────────────────────────────────────────────────

    /**
     * Recalcula juros e multa de todas as parcelas considerando a conta
     * de recebimento e a data de recebimento atuais do formulário.
     * Chamado ao trocar qualquer um dos dois campos.
     */
    private recalcularEncargosParaTodasParcelas(): void {
        if (!this.parcelasPreparadas.length) return;

        const idContaFinanceira = this.form.get('idContaFinanceira')?.value;
        const dataRecebimentoRaw = this.form.get('dataRecebimento')?.value;
        if (!dataRecebimentoRaw) return;

        const dataRecebimento = new Date(dataRecebimentoRaw);
        const contaSelecionada = idContaFinanceira
            ? this.data.listaContasFinanceiras.find(c => c.idContaFinanceira === idContaFinanceira) ?? null
            : null;

        this.parcelasPreparadas.forEach((parcelaOriginal, index) => {
            const ctrl = this.parcelasFormArray.at(index);
            if (!ctrl) return;

            const config = EncargosUtil.resolverConfiguracao(
                parcelaOriginal.hasJurosMultaFormaRecebimento,
                parcelaOriginal.percentualJurosDiarioFormaRecebimento,
                parcelaOriginal.percentualMultaMensalFormaRecebimento,
                parcelaOriginal.diasCarenciaMultaFormaRecebimento,
                contaSelecionada
            );

            const valorEmAberto = parcelaOriginal.valorPrincipalEmAberto;
            const vencimento = new Date(parcelaOriginal.vencimento);
            const liquidacoes = parcelaOriginal.liquidacoesExistentes ?? [];

            let novoJuros = 0;
            let novaMulta = 0;

            if (config) {
                novoJuros = EncargosUtil.calcularJuros(config, valorEmAberto, vencimento, dataRecebimento, liquidacoes);
                novaMulta = EncargosUtil.calcularMulta(config, valorEmAberto, vencimento, dataRecebimento, liquidacoes);
            }

            ctrl.patchValue({ valorJuros: novoJuros, valorMulta: novaMulta }, { emitEvent: false });
            this.calcularTotalCard(index);
        });
    }

    // ─── Cálculo de Totais ────────────────────────────────────────────────────

    private calcularTotal(): void {
        let total = 0;
        const parcelas = this.parcelasFormArray.getRawValue();
        parcelas.forEach(p => {
            total += Number(p.valorRecebido || 0);
        });
        this.totalRecebido = UtilService.arredondarAbnt(total, this.currencyMask.precision);
    }

    calcularTotalCard(index: number) {
        const item = this.parcelasFormArray.at(index);
        const aReceber = item.get('valorAReceber')?.value || 0;
        const juros = item.get('valorJuros')?.value || 0;
        const multa = item.get('valorMulta')?.value || 0;
        const desc = item.get('valorDesconto')?.value || 0;
        const acr = item.get('valorAcrescimo')?.value || 0;

        let recebido = aReceber + juros + multa + acr - desc;
        recebido = UtilService.arredondarAbnt(recebido, this.currencyMask.precision);

        let encargosMinimos = UtilService.arredondarAbnt(juros + multa + acr - desc, this.currencyMask.precision);
        if (encargosMinimos < 0) encargosMinimos = 0;

        item.get('valorRecebido')?.setValidators([Validators.required, Validators.min(encargosMinimos)]);
        item.get('valorRecebido')?.setValue(recebido >= 0 ? recebido : 0, { emitEvent: false });
        item.get('valorRecebido')?.updateValueAndValidity({ emitEvent: false });

        this.calcularTotal();
    }

    // ─── Salvar ───────────────────────────────────────────────────────────────

    salvar(): void {
        if (this.form.invalid) {
            this.form.markAllAsTouched();
            this.alertService.show('Atenção', 'Preencha todos os campos obrigatórios corretamente.', AlertType.warning);
            return;
        }

        const parcelasEnviadas = this.parcelasFormArray.getRawValue();
        const algumaZerada = parcelasEnviadas.some((p: any) => p.valorRecebido <= 0);
        if (algumaZerada) {
            this.alertService.show('Atenção', 'O valor recebido deve ser maior que zero em todos os documentos.', AlertType.warning);
            return;
        }

        const formValue = this.form.getRawValue();
        let parsedHtml = '';
        if (formValue.observacoesHtml) {
            parsedHtml = typeof formValue.observacoesHtml === 'object' ? toHTML(formValue.observacoesHtml) : formValue.observacoesHtml;
        }

        const request: RecebimentoContasReceberRequest = {
            idContaFinanceira: formValue.idContaFinanceira,
            dataRecebimento: formValue.dataRecebimento,
            observacoesHtml: parsedHtml,
            parcelas: formValue.parcelas.map((val: any) => {
                const itemReq: RecebimentoParcelaItemRequest = {
                    idContasReceberParcela: val.idContasReceberParcela,
                    idCategoriaFinanceira: val.idCategoriaFinanceira,
                    valorRecebido: val.valorRecebido,
                    valorJuros: val.valorJuros,
                    valorMulta: val.valorMulta,
                    valorDesconto: val.valorDesconto,
                    valorAcrescimo: val.valorAcrescimo
                };
                return itemReq;
            })
        };

        // Conta de recebimento diferente da conta da cobrança → o pagamento entrou por fora;
        // a cobrança ativa será cancelada/baixada. Avisa o usuário antes de confirmar.
        if (this.contaRecebimentoDifereDaCobranca(request.idContaFinanceira)) {
            this.dialog.open(ConfirmModalComponent, {
                width: '460px',
                data: {
                    title: 'Atenção — Cobrança ativa',
                    innerHtml: 'A conta de recebimento é diferente da conta da cobrança. Ao confirmar, a cobrança ativa (PIX/boleto) será cancelada/baixada para evitar pagamento em duplicidade.<br><br>Boletos de bancos que não permitem baixa automática precisarão ser baixados manualmente no portal do banco.<br><br>Deseja continuar?',
                    buttonConfirm: 'Continuar',
                    buttonClose: 'Voltar'
                }
            }).afterClosed().subscribe(r => { if (r?.confirm) this.executarRecebimento(request); });
        } else {
            this.executarRecebimento(request);
        }
    }

    /** Heurística do alerta prévio (o gate real é no backend, contra a conta do registro da cobrança). */
    private contaRecebimentoDifereDaCobranca(idContaRecebimento: number): boolean {
        return this.data.parcelas.some(p => {
            const boletoAtivo = p.idStatusBoleto === StatusIntegracaoBoletoEnum.Registrado
                || p.idStatusBoleto === StatusIntegracaoBoletoEnum.Pendente;
            const temCobrancaAtiva = boletoAtivo || p.idStatusPix != null;
            return temCobrancaAtiva && p.idContaFinanceira != null && p.idContaFinanceira !== idContaRecebimento;
        });
    }

    private executarRecebimento(request: RecebimentoContasReceberRequest): void {
        this.isSaving = true;
        this.recebimentoService.post(request).subscribe({
            next: (res: any) => {
                this.isSaving = false;
                if (res.isError) {
                    this.alertService.show('Aviso', res.errorDescription, AlertType.warning);
                    return;
                }
                this.alertService.show('Sucesso', 'Recebimento realizado com sucesso!', AlertType.success);
                this.dialogRef.close({ sucesso: true, avisoDescricao: res.avisoDescricao });
            },
            error: (err: any) => {
                this.isSaving = false;
                this.alertService.show('Erro', err.message || 'Erro ao processar Recebimento.', AlertType.error);
            }
        });
    }

    fechar(): void {
        this.dialogRef.close();
    }
}
